import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import {
  DrawerNavigation,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  DataRoomMember,
  DataRoomMembers,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import DataRoomMemberInfo from 'router/subrouters/DataRoom/components/DataRoomMemberInfo';
import QaRolesCustomSelect from 'components/QaRolesCustomSelect';
import { Button, Loader } from 'ncoded-component-library';
import TrashIcon from 'icons/Trash.icon';
import { useTranslation } from 'react-i18next';
import useOptions from 'router/subrouters/DataRoom/subrouters/Members/hooks/useOptions';
import MemberRoles from 'router/subrouters/DataRoom/components/MemberRoles';
import useDeleteMember from 'router/subrouters/DataRoom/hooks/useDeleteMember';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import KeyActions from 'router/subrouters/DataRoom/components/KeyActions';

import './ManageTeamMemberDrawer.styles.scss';

type ManageTeamMemberDrawerProps = {
  className?: string;
  member: DataRoomMember;
  updateMember?: (member: DataRoomMember) => void;
  updateDataRoomMembers?: (members: DataRoomMembers) => void;
} & DrawerNavigation;

const ManageTeamMemberDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageTeamMemberDrawerProps
> = (props, ref) => {
  const {
    className,
    member,
    updateMember,
    updateDataRoomMembers,
    onPrev,
    onNext,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { buyerQaRole } = useOptions();

  const { popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('manage-team-member-drawer', className);

  const { t } = useTranslation();

  const deleteMember = useDeleteMember();

  const handleRemoveMember = useCallback(async () => {
    deleteMember({
      member,
      setIsLoading,
      onSuccess: (data) => {
        updateDataRoomMembers?.(data);
        drawer?.setOpen(false);
      },
    });
  }, [deleteMember, drawer, member, updateDataRoomMembers]);

  const onSubmit = useCallback(
    async (values: Partial<DataRoomMember>) => {
      try {
        setIsLoading(true);

        await dataRoomApi.members.updateMember(member?._id, values);

        updateMember?.({ ...member, ...values });
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [member, popServerError, updateMember],
  );

  return (
    <SideDrawer
      ref={drawerRef}
      className={classes}
      onPrev={onPrev}
      onNext={onNext}
    >
      <DataRoomMemberInfo className="white-panel" member={member} isBuySide />

      {member?.requestedDataRoomAccess && (
        <MemberRoles defaultOpen>
          <QaRolesCustomSelect
            name="qaRole"
            label={t('qaRole')}
            options={buyerQaRole}
            renderAsPortal
            value={member?.qaRole}
            onChange={onSubmit}
          />

          {isLoading && <Loader />}
        </MemberRoles>
      )}

      <KeyActions description={t('keyActionsDescription')}>
        <Button variant="solid" icon={TrashIcon} onClick={handleRemoveMember}>
          {t('deleteMember')}
        </Button>
      </KeyActions>
    </SideDrawer>
  );
};

export default forwardRef(ManageTeamMemberDrawer);
